import { supabase } from '../lib/supabase';
import { logError } from '../lib/logError';
import { criarCacheComTtl } from '../lib/cacheComTtl';

export const COMMISSION_RULES_KEY = 'commission_rules_config';

export const PAYMENT_CATEGORIES = [
    { value: 'a_vista', label: 'À vista' },
    { value: 'caixa_31_50', label: '31% ou mais de caixa na entrada' },
    { value: 'caixa_20_30', label: '20% a 30% de caixa na entrada' },
    { value: 'parcelado_cartao', label: 'Parcelado no cartão' },
    { value: 'parcelado_boleto', label: 'Parcelado no boleto' },
    { value: 'parcelado_pix', label: 'Parcelado no Pix' },
];

export const BONUS_TYPES = [
    { value: 'percentage_points', label: 'Somar percentual' },
    { value: 'multiplier', label: 'Multiplicar comissão' },
    { value: 'fixed', label: 'Bônus fixo por venda' },
];

export const PARTICIPANT_CONTEXTS = [
    { value: 'seller', label: 'Vendedor' },
    { value: 'pre_sale', label: 'Pré-venda' },
    { value: 'manager', label: 'Gestão comercial' },
    { value: 'social_seller', label: 'Social Seller (número coletado)' },
];

export const OFFICIAL_COMMISSION_VALID_FROM = '2026-07-01';
export const OFFICIAL_CASH_COLLECT_CARGO_KEYS = Object.freeze(['closer', 'gestor_comercial']);

export const OFFICIAL_CARGO_SALE_RATES = Object.freeze({
    closer: Object.freeze({ 1: 0.05, 2: 0.06, 3: 0.07 }),
    sdr: Object.freeze({ 1: 0.015, 2: 0.018, 3: 0.025 }),
    // Sobre a venda cujo número o Social Seller coletou (linha `Venda (Social Seller)`).
    social_seller: Object.freeze({ 1: 0.01 }),
    gestor_comercial: Object.freeze({ 1: 0.07 }),
});

export const OFFICIAL_MANAGER_OVERRIDE_RATES = Object.freeze({
    gestor_comercial: Object.freeze({ 1: 0.02 }),
});

export const CASH_COLLECT_PRODUCTS = Object.freeze([
    Object.freeze({
        slug: 'performance',
        label: 'Performance',
        referencePrice: 54000,
        tiers: Object.freeze([
            Object.freeze({ percent: 20, amount: 10800, bonus: 200 }),
            Object.freeze({ percent: 30, amount: 16200, bonus: 300 }),
            Object.freeze({ percent: 40, amount: 21600, bonus: 350 }),
            Object.freeze({ percent: 50, amount: 27000, bonus: 450 }),
            Object.freeze({ percent: 100, amount: 54000, bonus: 600 }),
        ]),
    }),
    Object.freeze({
        slug: 'expansao',
        label: 'Expansão',
        referencePrice: 25800,
        tiers: Object.freeze([
            Object.freeze({ percent: 20, amount: 5160, bonus: 100 }),
            Object.freeze({ percent: 30, amount: 7740, bonus: 150 }),
            Object.freeze({ percent: 40, amount: 10320, bonus: 200 }),
            Object.freeze({ percent: 50, amount: 12900, bonus: 250 }),
            Object.freeze({ percent: 100, amount: 25800, bonus: 300 }),
        ]),
    }),
    Object.freeze({
        slug: 'tracao',
        label: 'Tração',
        referencePrice: null,
        tiers: Object.freeze([
            Object.freeze({ percent: 100, amount: null, bonus: 100 }),
        ]),
    }),
]);

export const DEFAULT_COMMISSION_RULES_CONFIG = Object.freeze({
    version: 1,
    rules: [],
    updatedAt: null,
    updatedBy: null,
});

/**
 * Com prazo de validade. Antes era `let commissionRulesCache = null` sem expiração, e
 * `invalidateCommissionRulesCache` não tinha nenhum chamador de aplicação — só teste. Uma aba
 * que lesse a configuração antes de uma publicação ficava com ela para sempre. Ver o cabeçalho
 * de `lib/cacheComTtl.js` para o caso concreto que isto custou.
 */
const commissionRulesCache = criarCacheComTtl(async () => {
    const { data, error } = await supabase.from('app_settings').select('value').eq('key', COMMISSION_RULES_KEY).maybeSingle();
    if (error) {
        await logError(error, { fn: 'getCommissionRulesConfig' });
        throw error;
    }
    return normalizeCommissionRulesConfig(data?.value);
});

export function commissionCargoKey(cargo) {
    const value = String(cargo ?? '').trim().toLowerCase();
    if (value.includes('closer')) return 'closer';
    if (value.includes('farmer') || value === 'sdr' || value.includes('social seller')) return 'sdr';
    if (value.includes('gestor') || value.includes('supervisor')) return 'gestor_comercial';
    return value.replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') || 'outro';
}

export function commissionRuleCargoKey(cargo) {
    const value = String(cargo ?? '').trim().toLowerCase();
    if (value.includes('closer')) return 'closer';
    if (value.includes('sales farmer') || value === 'farmer') return 'sales_farmer';
    if (value.includes('social seller')) return 'social_seller';
    if (value === 'sdr' || value.startsWith('sdr ')) return 'sdr';
    if (value.includes('gestor comercial')) return 'gestor_comercial';
    if (value.includes('supervisor')) return 'supervisor';
    return value.replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') || 'outro';
}

export function officialSaleRateForCargo(cargo, level) {
    const rates = OFFICIAL_CARGO_SALE_RATES[commissionRuleCargoKey(cargo)];
    return rates?.[Number(level)] ?? null;
}

export function officialManagerOverrideRateForCargo(cargo, level) {
    const rates = OFFICIAL_MANAGER_OVERRIDE_RATES[commissionRuleCargoKey(cargo)];
    return rates?.[Number(level)] ?? null;
}

/** Shape que `resolverGestorDaVenda` devolve quando ninguém recebe gestão — mesmas chaves, tudo zero. */
const SEM_GESTOR_PESSOA = Object.freeze({ id: null, cargo: 'Gestor Comercial', cargo_id: null, comissao_venda: 0, comissao_venda_override: 0 });

export function resolveCommissionManagerRecipient({ sellerName, seller, managerResolution }) {
    if (commissionRuleCargoKey(seller?.cargo) === 'gestor_comercial') {
        return { nome: sellerName, pessoa: seller, selfManagedSale: true };
    }
    // Gestão incide sobre venda de LIDERADO — closer N1/N2. O Closer N3 é par do gestor (mesmos
    // 7%), não liderado: no gabarito de 15/09/2026 nenhuma venda do Closer N3 tem linha de
    // gestão, e o dono confirmou a regra em 2026-09-11. Nível desconhecido (null) segue
    // recebendo gestão: é o caso legado, e faltar gestão seria silencioso.
    if (commissionRuleCargoKey(seller?.cargo) === 'closer' && Number(seller?.nivel) >= 3) {
        return { nome: null, pessoa: { ...SEM_GESTOR_PESSOA }, selfManagedSale: false, motivo: 'closer-nivel-3' };
    }
    return { ...managerResolution, selfManagedSale: false };
}

const DIACRITICOS_NOME = /\p{M}/gu; // marcas combinantes, depois do NFD

/**
 * Forma canônica de comparação de NOME DE PESSOA: sem diacrítico, minúsculo, espaço interno
 * colapsado, sem espaço nas pontas.
 *
 * ⛔ Deliberadamente NÃO é `normalizarRotulo` de `produtoResolver.js`, apesar de as duas
 * começarem iguais. São domínios diferentes que não podem co-variar: "Márcelo  Alves" e
 * "Marcelo Alves" são a mesma PESSOA (daí o `\s+ → ' '`), enquanto rótulo de produto compara
 * espaço a espaço porque um alias de produto é um token declarado, não um nome digitado por
 * alguém às pressas. Se um dia o resolvedor de produto mudar de regra, a identidade de pessoa
 * — que decide se alguém é pago duas vezes pela mesma venda — não pode mudar junto.
 */
export function commissionPersonKey(nome) {
    return String(nome ?? '')
        .normalize('NFD')
        .replace(DIACRITICOS_NOME, '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();
}

/**
 * A taxa vem do PAPEL na venda, não do cargo da pessoa (decisão do dono, 2026-08-31).
 *
 * ── O defeito, medido em produção em 2026-08-31 ──────────────────────────────────────────
 * Em 7 das 35 vendas do período o vendedor e o pré-vendedor eram a MESMA pessoa. Em 6 delas a
 * linha "Venda (Pré-venda)" pagou a taxa do papel de VENDEDOR, não a de pré-venda — porque
 * `submitVenda`/`submitVendaTicket` resolvem as duas taxas a partir do CARGO
 * (`customComissoes[commissionCargoKey(cargo)]`), e quando é a mesma pessoa os dois cargos são
 * o mesmo cargo. Um Closer que fez a própria pré-venda recebia `closer` duas vezes. Total do
 * período: R$ 7.817,67. Na venda E-commerce de R$ 28.200 a linha de pré-venda pagou 10%
 * (`closer`) quando o override do produto declara `sdr: 0,05`.
 *
 * ── A regra ──────────────────────────────────────────────────────────────────────────────
 * Quem vendeu E fez a pré-venda recebe SÓ o que está setado no cargo dele: a linha de
 * pré-venda NÃO NASCE. Nenhuma linha — nem com valor zero, nem com a taxa "certa" de SDR.
 * Exceção real (alguém que de fato merece o adicional) entra por adição MANUAL de comissão,
 * fora do motor, onde fica registrada com autor e data.
 *
 * ── Como se decide "mesma pessoa" ────────────────────────────────────────────────────────
 * `pessoa_id` quando os DOIS lados têm id — é a identidade forte, e dois cadastros distintos
 * com o mesmo nome continuam sendo duas pessoas. Só quando algum id falta (pessoa sem cadastro
 * em `bd_pessoas`, closer sem alias em `ghl_pessoa_alias`) cai no nome canônico, que é
 * heurística e por isso vem em segundo lugar.
 *
 * Devolve `{ gerar, motivo, criterio }`. `gerar: false` com `motivo: 'sem-pre-vendedor'` é o
 * caso de sempre (venda sem pré-venda) e não é anomalia; `'mesma-pessoa-que-vendedor'` é o que
 * merece log, porque é ausência de linha que alguém pode confundir com perda silenciosa.
 */
export function resolvePreSaleParticipation({ sellerName, sellerId = null, preSaleName, preSaleId = null }) {
    if (!String(preSaleName ?? '').trim()) {
        return { gerar: false, motivo: 'sem-pre-vendedor', criterio: null };
    }

    if (sellerId && preSaleId) {
        return sellerId === preSaleId
            ? { gerar: false, motivo: 'mesma-pessoa-que-vendedor', criterio: 'pessoa_id' }
            : { gerar: true, motivo: 'papeis-distintos', criterio: 'pessoa_id' };
    }

    const chavePreVenda = commissionPersonKey(preSaleName);
    const chaveVendedor = commissionPersonKey(sellerName);
    if (chavePreVenda && chavePreVenda === chaveVendedor) {
        return { gerar: false, motivo: 'mesma-pessoa-que-vendedor', criterio: 'nome_canonico' };
    }
    return { gerar: true, motivo: 'papeis-distintos', criterio: 'nome_canonico' };
}

export function buildOfficialCashCollectRules({ cargoKeys = OFFICIAL_CASH_COLLECT_CARGO_KEYS, validFrom = OFFICIAL_COMMISSION_VALID_FROM } = {}) {
    const recipients = stringList(cargoKeys);
    return CASH_COLLECT_PRODUCTS.flatMap((product) => product.tiers.map((tier) => ({
        id: `official-cash-collect-${product.slug}-${tier.percent}`,
        name: `${product.label} · ${tier.percent}% de cash collect`,
        active: true,
        type: 'fixed',
        value: tier.bonus,
        cargoKeys: recipients,
        participantContexts: ['seller'],
        productSlugs: [product.slug],
        paymentCategories: [],
        // A faixa é definida pelo PERCENTUAL do contrato recebido no primeiro mês. O valor em
        // reais de `tier.amount` é o mesmo percentual aplicado ao preço de tabela — serve para
        // a tela mostrar "entrada mínima", e nunca deveria decidir o match.
        //
        // Enquanto decidia, o teste percentual ficava DESLIGADO (`tier.amount == null ? ... : null`)
        // e toda venda fora do preço de tabela caía na faixa errada: R$ 108.000 com R$ 54.000
        // (50%) pagava R$ 600 com o rótulo "100%", e R$ 32.400 pago integralmente parava em
        // R$ 450 — o closer recebia menos justamente por ter trazido todo o caixa. Pior, o nome
        // gravado em `bd_comissoes` mentia sobre o percentual para quem lê o extrato.
        minCashPercent: tier.percent,
        maxCashPercent: null,
        minCashAmount: null,
        maxCashAmount: null,
        validFrom,
        validTo: null,
        priority: 100,
        exclusiveGroup: `cash-collect:${product.slug}`,
        exclusiveRank: tier.percent,
    })));
}

export function calculateFirstMonthCash(entries) {
    const normalized = (Array.isArray(entries) ? entries : [])
        .map((entry) => ({
            amount: Math.max(0, finiteNumber(entry?.valor ?? entry?.amount)),
            date: String(entry?.data ?? entry?.date ?? '').slice(0, 10),
        }))
        .filter((entry) => entry.amount > 0 && /^\d{4}-\d{2}-\d{2}$/.test(entry.date));
    if (!normalized.length) return 0;
    const firstMonth = normalized.map((entry) => entry.date.slice(0, 7)).sort()[0];
    return normalized
        .filter((entry) => entry.date.startsWith(firstMonth))
        .reduce((total, entry) => total + entry.amount, 0);
}

function finiteNumber(value, fallback = 0) {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
}

function stringList(value) {
    return Array.isArray(value) ? value.map(String).map((item) => item.trim()).filter(Boolean) : [];
}

export function normalizeCommissionRule(rule, index = 0) {
    const type = BONUS_TYPES.some((option) => option.value === rule?.type) ? rule.type : 'percentage_points';
    return {
        id: String(rule?.id || `rule-${index + 1}`),
        name: String(rule?.name || `Regra ${index + 1}`).trim(),
        active: rule?.active !== false,
        type,
        value: Math.max(0, finiteNumber(rule?.value)),
        cargoKeys: stringList(rule?.cargoKeys),
        participantContexts: stringList(rule?.participantContexts),
        productSlugs: stringList(rule?.productSlugs),
        paymentCategories: stringList(rule?.paymentCategories),
        minCashPercent: rule?.minCashPercent === '' || rule?.minCashPercent == null ? null : Math.max(0, finiteNumber(rule.minCashPercent)),
        maxCashPercent: rule?.maxCashPercent === '' || rule?.maxCashPercent == null ? null : Math.max(0, finiteNumber(rule.maxCashPercent)),
        minCashAmount: rule?.minCashAmount === '' || rule?.minCashAmount == null ? null : Math.max(0, finiteNumber(rule.minCashAmount)),
        maxCashAmount: rule?.maxCashAmount === '' || rule?.maxCashAmount == null ? null : Math.max(0, finiteNumber(rule.maxCashAmount)),
        validFrom: String(rule?.validFrom || '').slice(0, 10) || null,
        validTo: String(rule?.validTo || '').slice(0, 10) || null,
        priority: Math.trunc(finiteNumber(rule?.priority, 100)),
        exclusiveGroup: String(rule?.exclusiveGroup || '').trim() || null,
        exclusiveRank: rule?.exclusiveRank === '' || rule?.exclusiveRank == null ? null : finiteNumber(rule.exclusiveRank),
    };
}

export function normalizeCommissionRulesConfig(raw) {
    return {
        version: 1,
        rules: Array.isArray(raw?.rules) ? raw.rules.map(normalizeCommissionRule) : [],
        updatedAt: raw?.updatedAt || null,
        updatedBy: raw?.updatedBy || null,
    };
}

export function matchesCommissionRule(rule, context) {
    if (!rule.active) return false;
    const eventDate = String(context.eventDate ?? '').slice(0, 10);
    if (rule.validFrom && eventDate && eventDate < rule.validFrom) return false;
    if (rule.validTo && eventDate && eventDate > rule.validTo) return false;
    if (rule.cargoKeys.length && !rule.cargoKeys.includes(commissionRuleCargoKey(context.cargo))) return false;
    if (rule.participantContexts.length && !rule.participantContexts.includes(context.participantContext)) return false;
    if (rule.productSlugs.length && !rule.productSlugs.includes(context.productSlug)) return false;
    if (rule.paymentCategories.length && !rule.paymentCategories.includes(context.paymentCategory)) return false;

    const contractTotal = Math.max(0, finiteNumber(context.contractTotal));
    const firstMonthCash = Math.max(0, finiteNumber(context.firstMonthCash));
    const cashPercent = contractTotal > 0 ? (firstMonthCash / contractTotal) * 100 : 0;
    if (rule.minCashPercent != null && cashPercent < rule.minCashPercent) return false;
    if (rule.maxCashPercent != null && cashPercent > rule.maxCashPercent) return false;
    if (rule.minCashAmount != null && firstMonthCash < rule.minCashAmount) return false;
    if (rule.maxCashAmount != null && firstMonthCash > rule.maxCashAmount) return false;
    return true;
}

export function resolveCommissionPolicy(context, rawConfig = DEFAULT_COMMISSION_RULES_CONFIG) {
    const config = normalizeCommissionRulesConfig(rawConfig);
    const baseRate = Math.max(0, finiteNumber(context.baseRate));
    let effectiveRate = baseRate;
    const matchedRules = [];
    const fixedBonuses = [];
    const rules = [...config.rules].sort((a, b) => a.priority - b.priority || a.name.localeCompare(b.name));
    const matchingRules = rules.filter((rule) => matchesCommissionRule(rule, context));
    const selectedRules = [];
    const exclusiveWinners = new Map();

    for (const rule of matchingRules) {
        if (!rule.exclusiveGroup) {
            selectedRules.push(rule);
            continue;
        }
        const current = exclusiveWinners.get(rule.exclusiveGroup);
        const rank = rule.exclusiveRank ?? rule.minCashAmount ?? rule.minCashPercent ?? 0;
        const currentRank = current?.exclusiveRank ?? current?.minCashAmount ?? current?.minCashPercent ?? -Infinity;
        if (!current || rank > currentRank || (rank === currentRank && rule.priority < current.priority)) {
            exclusiveWinners.set(rule.exclusiveGroup, rule);
        }
    }
    selectedRules.push(...exclusiveWinners.values());
    selectedRules.sort((a, b) => a.priority - b.priority || a.name.localeCompare(b.name));

    for (const rule of selectedRules) {
        matchedRules.push(rule);
        if (rule.type === 'percentage_points') effectiveRate += rule.value / 100;
        else if (rule.type === 'multiplier') effectiveRate *= rule.value;
        else if (rule.type === 'fixed' && rule.value > 0) fixedBonuses.push({ id: rule.id, name: rule.name, amount: rule.value });
    }

    return {
        baseRate,
        effectiveRate: Math.max(0, effectiveRate),
        fixedBonuses,
        matchedRules,
    };
}

export function calculateCommissionScenario(context, rawConfig = DEFAULT_COMMISSION_RULES_CONFIG) {
    const policy = resolveCommissionPolicy(context, rawConfig);
    const commissionBase = Math.max(0, finiteNumber(context.commissionBase));
    const variableCommission = commissionBase * policy.effectiveRate;
    const fixedCommission = policy.fixedBonuses.reduce((total, bonus) => total + bonus.amount, 0);
    return {
        ...policy,
        commissionBase,
        variableCommission,
        fixedCommission,
        totalCommission: variableCommission + fixedCommission,
    };
}

export async function getCommissionRulesConfig() {
    return commissionRulesCache.ler();
}

export async function saveCommissionRulesConfig(rawConfig) {
    const config = normalizeCommissionRulesConfig(rawConfig);
    const { data: { user } } = await supabase.auth.getUser();
    const value = {
        ...config,
        updatedAt: new Date().toISOString(),
        updatedBy: user?.email ?? null,
    };
    const { error } = await supabase.from('app_settings').upsert({
        key: COMMISSION_RULES_KEY,
        value,
        updated_at: value.updatedAt,
    }, { onConflict: 'key' });
    if (error) {
        await logError(error, { fn: 'saveCommissionRulesConfig' });
        throw error;
    }
    // Quem acabou de publicar não espera o prazo: invalida e a próxima leitura busca do banco,
    // já com o que foi gravado. Escrever o valor direto no cache pularia a normalização que o
    // caminho de leitura aplica, e as duas abas passariam a divergir na forma do objeto.
    commissionRulesCache.invalidar();
    return value;
}

export function invalidateCommissionRulesCache() {
    commissionRulesCache.invalidar();
}
